import React, { useEffect, useRef, useState } from 'react'
import { Box } from '@mui/system'
import VideoPlayer from '../../../../components/videoPlayer'
import { Body1, H4 } from '../../../../layouts/TextStyles'
import { useAppDispatch } from '../../../../app/hooks'
import { usePrevious } from '../../../../app/hooks/usePrevious'
import { videoLessonApiSlice } from '../../../../features/section/videoLessonSlice'
import { SectionDetailsState } from '../../../../features/types'
import { sectionApiSlice } from '../../../../features/section/sectionSlice'
import { topicApiSlice } from '../../../../features/topic/topicSlice'

interface Props {
    videoDetails: SectionDetailsState
    videoLessonId: number
    userTopicEnrollId: number
    userSubjectEnrollId: number
}

const VideoContent: React.FC<Props> = ({
    videoDetails,
    videoLessonId,
    userTopicEnrollId,
    userSubjectEnrollId,
}) => {
    let interval: ReturnType<typeof setInterval> | undefined
    const dispatch = useAppDispatch()
    const watchedVideoDataIdRef = useRef(null)
    const currentTimeRef = useRef(null)
    const [isPaused, setIsPaused] = useState<boolean>(true)
    const [startedAt, setStartedAt] = useState<number>(0)
    const [currentTime, setCurrentTime] = useState<number>(null)
    const prevVideoLessonId = usePrevious(videoLessonId)
    const prevUserTopicEnrollId = usePrevious(userTopicEnrollId)
    const [startPlaying] =
        videoLessonApiSlice.useStartWatchingVideoLessonMutation()
    const [updatePlaying] = videoLessonApiSlice.useUpdatePlayingTimeMutation()

    const handleStartPlaying = async () => {
        if (!videoDetails.link) {
            return
        }

        const videoLink = new URL(videoDetails?.link)
        const startedAt = videoLink.searchParams.get('t')
            ? +videoLink.searchParams.get('t')
            : 0

        const result = await startPlaying({
            videoLessonId,
            userTopicEnrollId,
            watchedVideoId: videoDetails.watchedVideoId,
            startedAt: startedAt,
        }).unwrap()

        watchedVideoDataIdRef.current = result.watchedVideoDataId
        currentTimeRef.current = startedAt ? startedAt : null
        setCurrentTime(startedAt ? startedAt : null)
        setStartedAt(startedAt)
    }

    const handleMovingPlaying = async (currentTime: number) => {
        const result = await startPlaying({
            videoLessonId,
            userTopicEnrollId,
            watchedVideoId: videoDetails.watchedVideoId,
            watchedVideoDataId: watchedVideoDataIdRef.current,
            startedAt: currentTime,
        }).unwrap()

        watchedVideoDataIdRef.current = result.watchedVideoDataId
        setStartedAt(currentTime)
    }

    const handleProgressPlaying = async (e: CustomEvent<number>) => {
        if (!isPaused) {
            const playingSeconds = e.detail

            if (isValidPlaying) {
                const isMoved =
                    playingSeconds > currentTime + 3 ||
                    playingSeconds < startedAt

                if (isMoved && currentTime != null) {
                    handleMovingPlaying(playingSeconds)
                }
            }

            currentTimeRef.current = playingSeconds
            setCurrentTime(playingSeconds)
        }
    }

    const isValidPlaying = (playingSeconds: number) => {
        return (
            playingSeconds &&
            watchedVideoDataIdRef.current &&
            Math.trunc(playingSeconds) !== Math.trunc(currentTime) &&
            Math.trunc(playingSeconds) !== Math.trunc(currentTime) - 1
        )
    }

    const changeSectionPercentage = (
        watchedVideoPercentage: number,
        isLessonChanged: boolean,
    ) => {
        const videoLesson = isLessonChanged ? prevVideoLessonId : videoLessonId
        const userTopicEnroll = isLessonChanged
            ? prevUserTopicEnrollId
            : userTopicEnrollId

        dispatch(
            sectionApiSlice.util.updateQueryData(
                'getStudentSectionsByTopicEnroll',
                userTopicEnroll,
                (draftSections) => {
                    const section = draftSections.find(
                        (el) => el.id === videoLesson,
                    )

                    section.percentage = Math.trunc(watchedVideoPercentage)
                },
            ),
        )
    }

    const changeTopicPercentage = (
        topicEnrollPercentage: number,
        isLessonChanged: boolean,
    ) => {
        const userTopicEnroll = isLessonChanged
            ? prevUserTopicEnrollId
            : userTopicEnrollId

        dispatch(
            topicApiSlice.util.updateQueryData(
                'getStudentTopicsBySubject',
                userSubjectEnrollId,
                (draftTopics) => {
                    const topic = draftTopics.find(
                        (el) => el.userTopicEnrollId === userTopicEnroll,
                    )

                    topic.percentage = Math.trunc(topicEnrollPercentage)
                },
            ),
        )
    }

    const handleUpdatePlayingTime = async (isLessonChanged: boolean) => {
        const result = await updatePlaying({
            watchedVideoDataId: watchedVideoDataIdRef.current,
            timeAt: currentTimeRef.current,
        }).unwrap()

        changeSectionPercentage(result.watchedVideoPercentage, isLessonChanged)
        changeTopicPercentage(result.topicEnrollPercentage, isLessonChanged)
    }

    const handlePaused = (e: CustomEvent<boolean>) => {
        setIsPaused(e.detail)
    }

    useEffect(() => {
        if (!isPaused) {
            interval = setInterval(() => {
                handleUpdatePlayingTime(false)
            }, 10000)
        }
        return () => {
            clearInterval(interval)
        }
    }, [isPaused])

    useEffect(() => {
        return () => {
            if (watchedVideoDataIdRef.current) {
                handleUpdatePlayingTime(true)
                clearInterval(interval)
            }
        }
    }, [])

    return (
        <Box>
            <VideoPlayer
                currentTime={currentTimeRef.current}
                key={videoDetails.link}
                link={videoDetails.link}
                onStart={handleStartPlaying}
                onProgress={handleProgressPlaying}
                onPaused={handlePaused}
            />

            <Box>
                <H4>{videoDetails.title}</H4>
            </Box>

            <Box>
                <Body1>{videoDetails.description}</Body1>
            </Box>
        </Box>
    )
}

export default VideoContent
